// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material';
import { clientLogger } from '@seedplanner/logger';
import type { LostTraysReasonType } from '@seedplanner/shared-utils';
import {
  LOST_TRAYS_REASON,
  LOST_TRAYS_REASON_VALUES,
  PLANTING_STATE,
  SURPLUS_OPTION_ORDER_ID,
  upperCaseFirst,
} from '@seedplanner/shared-utils';
import { tryParseNumber } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import React, { useMemo, useCallback } from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '../../lib/base';
import Help from '../../lib/help/Help';
import type {
  RecordLostTraysCropDataQuery,
  RecordLostTraysCropDataQueryVariables,
  RecordLostTraysDataQuery,
  RecordLostTraysDataQueryVariables,
  RecordLostTraysMutation,
  RecordLostTraysMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, getID, getNumericID, validateNumInput } from '../../utils';
import { formatNumber } from '../../utils/formatNumber';
import { ExitDialogButton } from '../common/button';
import DialogInfoTitle from '../common/dailog/DailogInfoTitle';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import WhichTraysToPullTable, { type LostTraysOption } from './WhichTraysToPullTable';

const RECORD_LOST_TRAYS_MUTATION = gql`
  mutation RecordLostTrays($planting_id: Int!, $trays_lost: Int!, $reason: String, $notes: String, $pull_from: [RecordLostTraysPullFromInput!]!) {
    recordLostTrays(input: { planting_id: $planting_id, trays_lost: $trays_lost, reason: $reason, notes: $notes, pull_from: $pull_from }) {
      lost_trays_created {
        id
      }
      plantings_updated {
        id
        planting_status
        trays_rounded
        updated_at
      }
      orders_updated {
        id
        order_status
        updated_at
      }
    }
  }
`;

const RECORD_LOST_TRAYS_DATA_QUERY = gql`
  query RecordLostTraysData($id: ID!) {
    plantingGroup(id: $id) {
      plantings {
        id
        trays_rounded
        planting_status
        planting_state
        order {
          id
          customer
        }
      }
    }
  }
`;

const RECORD_LOST_TRAYS_CROP_DATA_QUERY = gql`
  query RecordLostTraysCropData {
    crops(
      orderBy: [{ name: "crop.crop_name", direction: "asc" }]
    ) {
      edges {
        node {
          id
          crop_name
          plantings(
            filters: [
              { name: "planting.planting_status", operator: "=", value: "in_progress" }
            ]
          ) {
            edges {
              node {
                id
                trays_rounded
                planting_status
                planting_state
                planted_at
                order {
                  id
                  customer
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly plantingId?: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

interface PlantingData {
  readonly id: string;
  readonly trays_rounded: string;
  readonly planting_status: string;
  readonly planting_state: string;
  readonly planted_at: number;
  readonly order?: {
    readonly id: string;
    readonly customer: string;
  } | null;
}

export default function RecordLostTraysDialog({ isOpen, onClose, onSuccess, plantingId }: Props) {
  const { language } = useUserContext();
  const [getPlantingGroup, { loading: plantingGroupLoading, data: plantingGroupData }] = useLazyQuery<
    RecordLostTraysDataQuery,
    RecordLostTraysDataQueryVariables
  >(RECORD_LOST_TRAYS_DATA_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [getCropNames, { loading: cropNamesLoading, data: cropNamesData }] = useLazyQuery<
    RecordLostTraysCropDataQuery,
    RecordLostTraysCropDataQueryVariables
  >(RECORD_LOST_TRAYS_CROP_DATA_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });
  const [recordLostTrays, { loading: recordLostTraysLoading, error: recordLostTraysError }] = useMutation<
    RecordLostTraysMutation,
    RecordLostTraysMutationVariables
  >(RECORD_LOST_TRAYS_MUTATION);

  React.useEffect(() => {
    if (isOpen) {
      if (plantingId != null) {
        void getPlantingGroup({
          variables: {
            id: createID.planting(plantingId),
          },
          onError: (errorIn) => {
            clientLogger.error({ title: 'planting_group_notes_error', message: errorIn.message });
            enqueueSnackbar('Error getting planting group data!', {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      } else {
        void getCropNames({
          // TODO: could set the crop (and maybe planting) if there is only one crop
          onError: (errorIn) => {
            clientLogger.error({ title: 'crops_for_lost_trays_error', message: errorIn.message });
            enqueueSnackbar('Error getting crops!', {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
        });
      }
    }
  }, [isOpen, getPlantingGroup, getCropNames, plantingId]);

  const [cropIdToUse, setCropIdToUse] = React.useState<number | null>(null);
  const [plantingIdToUse, setPlantingIdToUse] = React.useState<number | null>(null);

  const [shouldRecord, setShouldRecord] = React.useState(true);
  const [notes, setNotes] = React.useState('');
  const [traysLost, setTraysLost] = React.useState('');
  const [traysValidation, setTraysValidation] = React.useState('');
  const traysNumber = React.useMemo(() => Number.parseFloat(traysLost) || 0, [traysLost]);
  // Order ID => trays to pull
  const [pullFromMap, setPullFromMap] = React.useState<Map<number, string>>(new Map());
  const [pullFromValidationMap, setPullFromValidationMap] = React.useState<Map<number, string>>(new Map());
  const [reason, setReason] = React.useState<LostTraysReasonType>(LOST_TRAYS_REASON.DISEASE);

  const onNotesChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  }, []);
  const onChangeSelectReason = useCallback((e: SelectChangeEvent<string>) => {
    setReason(e.target.value as LostTraysReasonType);
  }, []);
  const onChangePlantingId = useCallback((e: SelectChangeEvent<number>) => {
    const val = e.target.value;
    const newVal = typeof val === 'string' ? Number.parseInt(val) || null : val;
    setPlantingIdToUse(newVal);
    onClear();
  }, []);

  const crops = useMemo(() => cropNamesData?.crops?.edges.map(({ node }) => node) ?? [], [cropNamesData]);
  const selectableCrops = useMemo(() => crops.filter((crop) => crop.plantings.edges.length > 0), [crops]);
  const plantingGroupsPerCropMap = useMemo(
    () =>
      selectableCrops.reduce((acc, crop) => {
        const cropKey = getNumericID(crop.id);
        const plantings = crop.plantings.edges.map(({ node }) => node);
        // Key is planted_at time
        const newPlantingsMap = new Map<number, PlantingData[]>();
        for (const p of plantings) {
          const key = p.planted_at ?? -1;
          newPlantingsMap.set(key, [
            ...(newPlantingsMap?.get(key) ?? []),
            {
              ...p,
              planted_at: key,
            },
          ]);
        }

        // Create new map where key is the min planting ID
        const finalMap = new Map<number, PlantingData[]>();
        for (const values of newPlantingsMap.values()) {
          const minId = Math.min(...values.map((p) => getNumericID(p.id)));
          finalMap.set(minId, values);
        }

        acc.set(cropKey, finalMap);

        return acc;
      }, new Map<number, Map<number, PlantingData[]>>()),
    [selectableCrops],
  );

  const onChangeCropId = useCallback(
    (e: SelectChangeEvent<number>) => {
      const val = e.target.value;
      const newVal = typeof val === 'string' ? Number.parseInt(val) || null : val;
      setCropIdToUse(newVal);
      let didSetPlantign = false;
      if (newVal != null) {
        const plantingsMap = plantingGroupsPerCropMap.get(newVal);
        const plantingGroups = Array.from(plantingsMap?.values() ?? []);
        if (plantingGroups.length === 1) {
          const plantingsIn = plantingGroups[0];
          const minId = Math.min(...plantingsIn.map((p) => getNumericID(p.id)));
          didSetPlantign = true;
          setPlantingIdToUse(minId);
        }
      }
      if (!didSetPlantign) {
        setPlantingIdToUse(null);
      }
      onClear();
    },
    [plantingGroupsPerCropMap],
  );

  const selectablePlantingGroups: Map<number, PlantingData[]> = useMemo(
    () => (cropIdToUse != null ? (plantingGroupsPerCropMap.get(cropIdToUse) ?? new Map()) : new Map()),
    [cropIdToUse, plantingGroupsPerCropMap],
  );
  const selectedPlantingsFromGroup = useMemo(
    () => (plantingIdToUse == null ? [] : (selectablePlantingGroups.get(plantingIdToUse) ?? [])),
    [plantingIdToUse, selectablePlantingGroups],
  );
  const plantings = useMemo(
    () => (plantingId === undefined ? selectedPlantingsFromGroup : (plantingGroupData?.plantingGroup?.plantings ?? [])),
    [plantingId, plantingGroupData, selectedPlantingsFromGroup],
  );
  const allTraysAvailableNum = useMemo(
    () => plantings.reduce((acc, p) => acc.add(p.trays_rounded), new Decimal(0)).toNumber(),
    [plantings],
  );
  const surplusPlantings = useMemo(
    () => plantings.filter((p) => p.planting_state !== PLANTING_STATE.ALLOCATED),
    [plantings],
  );
  // TODO: later we want to parse out buffers using PLANTING_STATE.BUFFER
  const surplusAvailable = useMemo(
    () => surplusPlantings.reduce((acc, p) => acc.add(p.trays_rounded), new Decimal(0)),
    [surplusPlantings],
  );
  const traysAvailablePerOrder = useMemo(() => {
    const map = plantings.reduce(
      (acc, p) => {
        if (p.order != null) {
          const orderId = getNumericID(p.order.id);
          acc.set(orderId, {
            trays: (acc.get(orderId)?.trays ?? new Decimal(0)).add(p.trays_rounded),
            orderInfo: p.order,
          });
        }
        return acc;
      },
      new Map<
        number,
        {
          readonly trays: Decimal;
          readonly orderInfo: { readonly customer: string };
        }
      >(),
    );
    map.set(SURPLUS_OPTION_ORDER_ID, {
      trays: surplusAvailable,
      orderInfo: {
        customer: 'Surplus',
      },
    });

    return map;
  }, [plantings, surplusAvailable]);
  const options: readonly LostTraysOption[] = useMemo(
    () =>
      [
        // Make sure surplus option is always first
        {
          traysAvailable: surplusAvailable,
          orderInfo: {
            orderId: SURPLUS_OPTION_ORDER_ID,
            customer: 'Surplus',
          },
        },
        ...Array.from(traysAvailablePerOrder.entries())
          .filter(([orderId]) => orderId !== SURPLUS_OPTION_ORDER_ID)
          .map(([orderId, allInfo]) => ({
            traysAvailable: allInfo.trays,
            orderInfo: {
              orderId,
              customer: allInfo.orderInfo.customer,
            },
          })),
      ].filter((option) => option.traysAvailable.gt(0)),
    [surplusAvailable, traysAvailablePerOrder],
  );

  // If there's only one option then disable the pull from table
  // and just update the pull from map as the trays lost is updated
  const onlyOneOption = options.length === 1;

  const onChangePullFrom = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, orderId: number) => {
    const newMap = new Map(pullFromMap);
    newMap.set(orderId, e.target.value);
    setPullFromMap(newMap);

    const newValidationMap = new Map(pullFromValidationMap);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => newValidationMap.set(orderId, msg),
      validateGTE: 0,
      validateLTE: traysAvailablePerOrder.get(orderId)?.trays?.toNumber() ?? allTraysAvailableNum,
      isRequired: false,
      validateInt: false,
    });
    setPullFromValidationMap(newValidationMap);

    const traysSum = Array.from(newMap.values())
      .reduce((acc, trays) => acc.add(Number.parseFloat(trays) || 0), new Decimal(0))
      .toNumber();
    // Validate that the trays to pull is less than the trays available
    if (traysSum > traysNumber) {
      setTraysValidation('The sum of the trays to deduct cannot be greater than the number of trays lost');
    } else {
      setTraysValidation('');
    }
  };
  const onBlurPullFrom = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, orderId: number) => {
    const newValidationMap = new Map(pullFromValidationMap);
    newValidationMap.delete(orderId);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => newValidationMap.set(orderId, msg),
      validateGTE: 0,
      validateLTE: traysAvailablePerOrder.get(orderId)?.trays?.toNumber() ?? allTraysAvailableNum,
      isRequired: false,
      validateInt: false,
    });
    setPullFromValidationMap(newValidationMap);
  };
  const onChangeTrays = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTraysLost(e.target.value);
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setTraysValidation(msg),
        validateGT: 0,
        validateLTE: allTraysAvailableNum,
        isRequired: true,
        validateInt: true,
      });
      const maybeNum = tryParseNumber({ value: e.target.value, parseInt: true });
      if (maybeNum === undefined) {
        return;
      }

      if (onlyOneOption) {
        const onlyOptionId = options[0].orderInfo.orderId;
        const newMap = new Map(pullFromMap);
        newMap.set(onlyOptionId, maybeNum.toString());
        setPullFromMap(newMap);

        // Don't validate the sum because there's only one option
        return;
      }

      const traysSum = Array.from(pullFromMap.values())
        .reduce((acc, trays) => acc.add(Number.parseFloat(trays) || 0), new Decimal(0))
        .toNumber();
      if (traysSum > maybeNum) {
        setTraysValidation('The sum of the trays to pull cannot be greater than the number of trays lost');
      } else {
        setTraysValidation('');
      }
    },
    [allTraysAvailableNum, pullFromMap, onlyOneOption, options],
  );
  const onTraysBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setTraysValidation(msg),
        validateGT: 0,
        validateLTE: allTraysAvailableNum,
        isRequired: true,
        validateInt: true,
      });
    },
    [allTraysAvailableNum],
  );
  const onChangeShouldRecord = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldRecord(e.target.checked);
  };
  const onLostAllTrays = () => {
    setTraysLost(allTraysAvailableNum.toString());

    const newPullFromMap = new Map(pullFromMap);
    for (const [key, value] of traysAvailablePerOrder.entries()) {
      newPullFromMap.set(key, value.trays.toString());
    }
    setPullFromMap(newPullFromMap);

    setPullFromValidationMap(new Map());
    setTraysValidation('');
  };
  const onClear = () => {
    setTraysLost('');

    const newPullFromMap = new Map(pullFromMap);
    for (const [key] of traysAvailablePerOrder.entries()) {
      newPullFromMap.set(key, '0');
    }
    setPullFromMap(newPullFromMap);

    setPullFromValidationMap(new Map());
    setTraysValidation('');
  };

  const onSubmit = () => {
    void recordLostTrays({
      variables: {
        // Hacky way to get types to work
        planting_id: plantingId === undefined ? (plantingIdToUse ?? -1) : plantingId,
        notes: notes ?? undefined,
        trays_lost: traysNumber,
        pull_from: Array.from(pullFromMap.entries())
          .map(([orderId, trays]) => ({
            order_id: orderId === SURPLUS_OPTION_ORDER_ID ? null : orderId,
            trays_to_sub: trays === '' ? 0 : Number.parseFloat(trays) || 0,
          }))
          .filter((o) => o.trays_to_sub > 0),
        reason: shouldRecord ? reason : undefined,
      },
      update: (cache) => {
        cache.modify({
          fields: {
            lost_trayss(_, { DELETE }) {
              return DELETE;
            },
            harvests(_, { DELETE }) {
              return DELETE;
            },
            plantingGroup(_, { DELETE }) {
              return DELETE;
            },
            farm_report(_, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data !== undefined && data.recordLostTrays != null) {
          enqueueSnackbar('Recorded lost trays', { variant: 'success' });
          if (data.recordLostTrays.orders_updated.length > 0) {
            enqueueSnackbar(
              `These Orders are now "completed": ${data.recordLostTrays.orders_updated.map((o) => getID(o.id)).join(', ')}`,
              { variant: 'warning', autoHideDuration: 10000 },
            );
          }
          onSuccess();
        } else {
          enqueueSnackbar('Error recording lost trays!', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'planting_group_notes_error', message: errorIn.message });
      },
    });
  };

  const disableFields = recordLostTraysLoading;
  const isSubmitDisabled =
    plantingGroupLoading ||
    recordLostTraysLoading ||
    traysValidation !== '' ||
    traysLost === '' ||
    traysNumber <= 0 ||
    Array.from(pullFromValidationMap.values()).some((v) => v !== '') ||
    !Array.from(pullFromMap.values())
      .reduce((acc, trays) => acc.add(Number.parseFloat(trays) || 0), new Decimal(0))
      .eq(traysNumber);

  const disableTraysLostInput = plantingId === undefined && (cropIdToUse == null || plantingIdToUse == null);

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      {plantingId === undefined && !cropNamesLoading && selectableCrops.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.2rem' }}>
          There are in no progress planting groups in your farm!
        </Typography>
      ) : (
        <>
          {plantingId === undefined && (
            <>
              <FormControl fullWidth>
                <InputLabel id="crop-selector">Crop *</InputLabel>
                <Select
                  aria-describedby="crop-selector"
                  id="crop-selector"
                  labelId="crop-selector"
                  label="Crop *"
                  value={cropIdToUse ?? ''}
                  onChange={onChangeCropId}
                  required={true}
                  disabled={cropNamesLoading || disableFields}
                >
                  {selectableCrops.map((crop) => {
                    return (
                      <MenuItem key={crop.id} value={getNumericID(crop.id)}>
                        {crop.crop_name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="planting-group-selector">Planting Group *</InputLabel>
                <Select
                  aria-describedby="planting-group-selector"
                  id="planting-group-selector"
                  labelId="planting-group-selector"
                  label="Planting Group *"
                  value={plantingIdToUse ?? ''}
                  onChange={onChangePlantingId}
                  required={true}
                  disabled={cropIdToUse == null || cropNamesLoading || disableFields}
                >
                  {Array.from(selectablePlantingGroups.values()).map((plantings) => {
                    const totalTrays = plantings.reduce((acc, p) => acc.add(p.trays_rounded), new Decimal(0));
                    // Use the smallest ID in the group as the key
                    const minId = Math.min(...plantings.map((p) => getNumericID(p.id)));

                    return (
                      <MenuItem key={minId} value={minId}>
                        {`Planting Group ${minId} (${formatNumber(totalTrays.toNumber(), language)} trays)`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </>
          )}
          <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" gap={1}>
            <Box display="flex" flexDirection="column">
              <FormControl required sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <FormLabel id="should-record-label">Record Incident</FormLabel>
                <Switch checked={shouldRecord} onChange={onChangeShouldRecord} />
              </FormControl>
            </Box>
            <Help tooltip="Turn this off if you don't want to record the reason for the loss. This would be as if you never planted the lost trays. There will be no record of the loss." />
          </Box>
          {shouldRecord && (
            <FormControl fullWidth>
              <InputLabel id="reason-selector">Reason For Loss *</InputLabel>
              <Select
                id="reason-select"
                aria-describedby="reason-select"
                required
                value={reason}
                onChange={onChangeSelectReason}
                disabled={disableFields}
                fullWidth
                autoFocus
                label="Reason For Loss *"
              >
                {LOST_TRAYS_REASON_VALUES.map((name) => (
                  <MenuItem key={name} value={name}>
                    {upperCaseFirst(name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="flex-start"
            alignItems="center"
            gap={2}
          >
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={1}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <Button variant="outlined" onClick={onLostAllTrays} sx={{ flexGrow: 1 }}>
                All
              </Button>
              <Button variant="outlined" onClick={onClear} sx={{ flexGrow: 1 }}>
                Clear
              </Button>
            </Box>
            <TextField
              aria-describedby="lost-trays"
              id="lost-trays"
              label={
                disableTraysLostInput
                  ? 'Select crop and planting group first'
                  : `Number of Trays Lost (${allTraysAvailableNum} available)`
              }
              value={traysLost}
              onChange={onChangeTrays}
              onBlur={onTraysBlur}
              disabled={disableTraysLostInput || plantingGroupLoading || disableFields}
              hasSubtext
              required
              inputMode="numeric"
              type="number"
              numberMin={0}
              numberMax={allTraysAvailableNum}
              subtext={traysValidation}
              error={traysValidation !== ''}
            />
          </Box>
          {options.length > 0 && (
            <Box>
              <Box display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center" gap={1}>
                <DialogInfoTitle>Select Deductions</DialogInfoTitle>
                <Help tooltip="Select how this tray loss will be deducted from orders or surplus. The sum must equal the number of trays lost." />
              </Box>
              <Box>
                <Typography variant="body2">
                  {`Deduction Sum: ${Array.from(pullFromMap.values()).reduce(
                    (acc, trays) => acc.add(Number.parseFloat(trays) || 0),
                    new Decimal(0),
                  )}`}
                </Typography>
              </Box>
              <WhichTraysToPullTable
                disabled={onlyOneOption || plantingGroupLoading || disableFields}
                options={options}
                pullFromMap={pullFromMap}
                validationMap={pullFromValidationMap}
                onChangePullFrom={onChangePullFrom}
                onBlurPullFrom={onBlurPullFrom}
                allTraysAvailable={allTraysAvailableNum}
              />
            </Box>
          )}
          {shouldRecord && (
            <TextField
              aria-describedby="lost-trays-notes"
              id="lost-trays-notes"
              label="Notes"
              value={notes}
              onChange={onNotesChange}
              // onEnter={onEnter}
              disabled={disableFields}
            />
          )}
        </>
      )}
    </Box>
  );

  const dialogContent = plantingGroupLoading ? (
    <PageLoading />
  ) : recordLostTraysError ? (
    <ErrorView error={recordLostTraysError} />
  ) : (
    form
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Record Lost Trays</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
